import { supabase } from '../lib/supabase';

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  read: boolean;
  created_at: string;
  sender?: {
    id: string;
    name: string;
    company: string;
  };
  recipient?: {
    id: string;
    name: string;
    company: string;
  };
}

export interface Conversation {
  partner: {
    id: string;
    name: string;
    company: string;
  };
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
}

export class MessageService {
  // Send a message
  static async sendMessage(
    senderId: string,
    recipientId: string,
    content: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          sender_id: senderId,
          recipient_id: recipientId,
          content,
          read: false
        });

      if (error) {
        console.error('Error sending message:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, error: 'Failed to send message' };
    }
  }

  // Get conversation between two users
  static async getConversation(
    userId1: string,
    userId2: string
  ): Promise<Message[]> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:users!messages_sender_id_fkey(name, company)
        `)
        .or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching conversation:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return [];
    }
  }

  // Mark messages as read
  static async markMessagesAsRead(
    recipientId: string,
    senderId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ read: true })
        .eq('recipient_id', recipientId)
        .eq('sender_id', senderId)
        .eq('read', false);

      if (error) {
        console.error('Error marking messages as read:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return { success: false, error: 'Failed to mark messages as read' };
    }
  }

  // Get unread message count for a user
  // Get total unread count for user
  static async getTotalUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('read', false);

      if (error) {
        console.error('Error getting total unread count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error getting total unread count:', error);
      return 0;
    }
  }

  static async getUnreadCount(
    userId: string,
    partnerId: string
  ): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('sender_id', partnerId)
        .eq('read', false);

      if (error) {
        console.error('Error getting unread count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // Get all conversations for a user - OPTIMIZED VERSION
  static async getUserConversations(userId: string): Promise<Conversation[]> {
    try {
      // OPTIMIZATION: Simplified query with only essential fields
      const { data, error } = await supabase
        .from('messages')
        .select(`
          sender_id,
          recipient_id,
          content,
          created_at,
          read,
          sender:users!messages_sender_id_fkey(name, company),
          recipient:users!messages_recipient_id_fkey(name, company)
        `)
        .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
        .order('created_at', { ascending: false })
        .limit(200); // OPTIMIZATION: Limit to recent messages only

      if (error) {
        console.error('Error fetching conversations:', error);
        return [];
      }

      // OPTIMIZATION: More efficient grouping
      const conversationMap = new Map();
      
      data?.forEach(message => {
        const partnerId = message.sender_id === userId ? message.recipient_id : message.sender_id;
        const partner = message.sender_id === userId ? message.recipient : message.sender;
        
        if (!conversationMap.has(partnerId)) {
          conversationMap.set(partnerId, {
            partner: {
              id: partnerId,
              name: partner?.name || 'Unknown',
              company: partner?.company || ''
            },
            lastMessage: message.content,
            lastMessageTime: message.created_at,
            unreadCount: 0
          });
        }
        
        // Count unread messages from this partner
        if (message.recipient_id === userId && !message.read) {
          conversationMap.get(partnerId).unreadCount++;
        }
      });

      return Array.from(conversationMap.values());
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  }
}